import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownAZ,
  ArrowUpAZ,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  Plus,
  Scale,
  Search,
  X,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  apiClient,
  type ActionGroup,
  type ActionType,
  type CaseListItem,
  type CaseNature,
  type CaseStatus,
  type CasesListResponse,
  type CasesSummaryResponse,
  type Tribunal,
} from '@/lib/api-client';
import { GlassDateRangePicker } from '@/components/ui/glass-date-range-picker';
import { AnchoredPopover } from '@/components/ui/anchored-popover';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 20;

const STATUS_META: Record<CaseStatus, { label: string; cls: string }> = {
  active: {
    label: 'Ativo',
    cls: 'border-emerald-500/40 text-emerald-600 bg-emerald-500/10',
  },
  suspended: {
    label: 'Suspenso',
    cls: 'border-amber-500/40 text-amber-600 bg-amber-500/10',
  },
  archived: {
    label: 'Arquivado',
    cls: 'border-[var(--border)] text-[var(--fg-muted)] bg-[var(--surface-muted)]',
  },
  closed: {
    label: 'Encerrado',
    cls: 'border-[var(--border)] text-[var(--fg-muted)] bg-[var(--surface-muted)]',
  },
};

const SORTS: Array<{
  key: string;
  label: string;
  sortBy: 'createdAt' | 'openedAt' | 'numeroCnj';
  sortDir: 'asc' | 'desc';
}> = [
  {
    key: 'recent',
    label: 'Mais recentes',
    sortBy: 'createdAt',
    sortDir: 'desc',
  },
  {
    key: 'oldest',
    label: 'Mais antigos',
    sortBy: 'createdAt',
    sortDir: 'asc',
  },
  {
    key: 'opened_desc',
    label: 'Abertura (recente)',
    sortBy: 'openedAt',
    sortDir: 'desc',
  },
  {
    key: 'opened_asc',
    label: 'Abertura (antiga)',
    sortBy: 'openedAt',
    sortDir: 'asc',
  },
  {
    key: 'cnj_asc',
    label: 'CNJ (A→Z)',
    sortBy: 'numeroCnj',
    sortDir: 'asc',
  },
  {
    key: 'cnj_desc',
    label: 'CNJ (Z→A)',
    sortBy: 'numeroCnj',
    sortDir: 'desc',
  },
];

function formatCnj(cnj: string | null): string {
  if (!cnj) return 'Sem CNJ';
  const d = cnj.replace(/\D/g, '');
  if (d.length !== 20) return cnj;
  return `${d.slice(0, 7)}-${d.slice(7, 9)}.${d.slice(9, 13)}.${d.slice(13, 14)}.${d.slice(14, 16)}.${d.slice(16)}`;
}

function formatOpenedAt(value: string | null): string {
  if (!value) return '--';
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
  } catch {
    return '--';
  }
}

function StatusBadge({ status }: { status: CaseStatus }) {
  const meta = STATUS_META[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium',
        meta.cls,
      )}
    >
      {meta.label}
    </span>
  );
}

interface FilterState {
  status: CaseStatus | '';
  actionGroupId: string;
  actionTypeId: string;
  nature: CaseNature | '';
  tribunalCode: string;
  vara: string;
  openedFrom: string;
  openedTo: string;
}

const EMPTY_FILTERS: FilterState = {
  status: '',
  actionGroupId: '',
  actionTypeId: '',
  nature: '',
  tribunalCode: '',
  vara: '',
  openedFrom: '',
  openedTo: '',
};

const NATURE_OPTIONS: { value: CaseNature | ''; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: 'judicial', label: 'Judicial' },
  { value: 'extrajudicial', label: 'Extrajudicial' },
  { value: 'consultoria', label: 'Consultoria' },
  { value: 'administrativo', label: 'Administrativo' },
];

function FilterDropdown({
  filters,
  onChange,
}: {
  filters: FilterState;
  onChange: (next: Partial<FilterState>) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const groupsQ = useQuery<ActionGroup[]>({
    queryKey: ['action-groups', 'active'],
    queryFn: () => apiClient.actionGroupsList({ active: 'true' }),
    staleTime: 1000 * 60 * 5,
    enabled: open,
  });

  const typesQ = useQuery<ActionType[]>({
    queryKey: ['action-types', 'active', filters.actionGroupId],
    queryFn: () =>
      apiClient.actionTypesList({
        active: 'true',
        actionGroupId: filters.actionGroupId || undefined,
      }),
    staleTime: 1000 * 60 * 5,
    enabled: open,
  });

  const tribunalsQ = useQuery<Tribunal[]>({
    queryKey: ['tribunals', 'active'],
    queryFn: () => apiClient.tribunalsList({ active: 'true' }),
    staleTime: 1000 * 60 * 10,
    enabled: open,
  });

  // Conta apenas os campos do popover. openedFrom/openedTo viraram controle
  // separado na toolbar (GlassDateRangePicker), com proprio "Limpar".
  const POPOVER_FIELDS: (keyof FilterState)[] = [
    'status',
    'actionGroupId',
    'actionTypeId',
    'nature',
    'tribunalCode',
    'vara',
  ];
  const activeCount = POPOVER_FIELDS.filter(
    (k) => filters[k] !== '' && filters[k] !== undefined,
  ).length;

  const selectCls =
    'w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-2 py-1.5 text-xs text-[var(--fg)]';
  const inputCls =
    'w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-2 py-1.5 text-xs text-[var(--fg)] placeholder:text-[var(--fg-muted)]';
  const labelCls = 'mb-1 block font-medium text-[var(--fg)]';

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-tour="processos-filtrar"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition',
          'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
        )}
      >
        <Filter className="h-3.5 w-3.5" />
        Filtrar
        {activeCount > 0 && (
          <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-semibold text-[var(--accent-fg)]">
            {activeCount}
          </span>
        )}
      </button>
      <AnchoredPopover
        anchorRef={triggerRef}
        open={open}
        onClose={() => setOpen(false)}
        width={320}
        align="center"
      >
        <div className="flex flex-col gap-3 overflow-y-auto p-3 text-xs">
            {/* Status */}
            <div>
              <label className={labelCls}>Status</label>
              <select
                value={filters.status}
                onChange={(e) =>
                  onChange({ status: e.target.value as CaseStatus | '' })
                }
                className={selectCls}
              >
                <option value="">Todos</option>
                {(Object.keys(STATUS_META) as CaseStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_META[s].label}
                  </option>
                ))}
              </select>
            </div>

            {/* Grupo de acao + Tipo de acao */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Grupo de ação</label>
                <select
                  value={filters.actionGroupId}
                  onChange={(e) => {
                    onChange({
                      actionGroupId: e.target.value,
                      actionTypeId: '',
                    });
                  }}
                  className={selectCls}
                >
                  <option value="">Todos</option>
                  {(groupsQ.data ?? []).map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Tipo de ação</label>
                <select
                  value={filters.actionTypeId}
                  onChange={(e) =>
                    onChange({ actionTypeId: e.target.value })
                  }
                  className={selectCls}
                >
                  <option value="">Todos</option>
                  {(typesQ.data ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Natureza */}
            <div>
              <label className={labelCls}>Natureza</label>
              <select
                value={filters.nature}
                onChange={(e) =>
                  onChange({ nature: e.target.value as CaseNature | '' })
                }
                className={selectCls}
              >
                {NATURE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Tribunal */}
            <div>
              <label className={labelCls}>Tribunal</label>
              <select
                value={filters.tribunalCode}
                onChange={(e) =>
                  onChange({ tribunalCode: e.target.value })
                }
                className={selectCls}
              >
                <option value="">Todos</option>
                {(tribunalsQ.data ?? []).map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.abbreviation} - {t.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Vara */}
            <div>
              <label className={labelCls}>Vara</label>
              <input
                type="text"
                value={filters.vara}
                onChange={(e) => onChange({ vara: e.target.value })}
                placeholder="Ex: 3a Vara Cível"
                className={inputCls}
              />
            </div>

            {activeCount > 0 && (
              <button
                type="button"
                onClick={() =>
                  onChange({
                    status: '',
                    actionGroupId: '',
                    actionTypeId: '',
                    nature: '',
                    tribunalCode: '',
                    vara: '',
                  })
                }
                className="self-start text-[11px] font-medium text-[var(--accent)]"
              >
                Limpar filtros
              </button>
            )}
        </div>
      </AnchoredPopover>
    </div>
  );
}

function SortDropdown({
  current,
  onChange,
}: {
  current: string;
  onChange: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const currentSort = SORTS.find((s) => s.key === current) ?? SORTS[0]!;
  const Icon = currentSort.sortDir === 'asc' ? ArrowUpAZ : ArrowDownAZ;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition',
          'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
        )}
      >
        <Icon className="h-3.5 w-3.5" />
        Ordenar
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      <AnchoredPopover
        anchorRef={triggerRef}
        open={open}
        onClose={() => setOpen(false)}
        width={192}
        align="end"
      >
        <ul className="py-1">
          {SORTS.map((s) => (
            <li key={s.key}>
              <button
                type="button"
                onClick={() => {
                  onChange(s.key);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-left text-xs',
                  'text-[var(--fg)] hover:bg-[var(--surface-muted)]',
                  s.key === current && 'bg-[var(--surface-muted)] font-medium',
                )}
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      </AnchoredPopover>
    </div>
  );
}

function TableSkeleton() {
  return (
    <tbody>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i} className="border-b border-[var(--border)]">
          {Array.from({ length: 6 }).map((_, j) => (
            <td key={j} className="px-4 py-3">
              <div className="h-3 w-3/4 animate-pulse rounded bg-[var(--surface-muted)]" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

function EmptyRow() {
  return (
    <tbody>
      <tr>
        <td colSpan={6} className="px-4 py-16 text-center">
          <div className="flex flex-col items-center gap-3">
            <Scale
              className="h-10 w-10 text-[var(--fg-muted)] opacity-40"
              strokeWidth={1.5}
            />
            <p className="max-w-sm text-sm text-[var(--fg-muted)]">
              Nenhum processo cadastrado ainda. Comece adicionando o primeiro.
            </p>
            <Link
              to="/escritorio/processos/novo"
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition',
                'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
              )}
            >
              <Plus className="h-3.5 w-3.5" />
              Novo processo
            </Link>
          </div>
        </td>
      </tr>
    </tbody>
  );
}

function StatPill({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'active' | 'suspended' | 'archived' | 'closed';
}) {
  const cls = {
    active: 'border-emerald-500/40 text-emerald-600',
    suspended: 'border-amber-500/40 text-amber-600',
    archived: 'border-[var(--border)] text-[var(--fg-muted)]',
    closed: 'border-[var(--border)] text-[var(--fg-muted)]',
  }[tone];
  return (
    <div
      className={cn(
        'flex items-baseline gap-1.5 rounded-md border bg-[var(--surface)] px-3 py-1.5',
        cls,
      )}
    >
      <span className="text-base font-semibold tabular-nums">{value}</span>
      <span className="text-[11px] text-[var(--fg-muted)]">{label}</span>
    </div>
  );
}

export default function ProcessosPage() {
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState<string>('recent');

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const sort = useMemo(
    () => SORTS.find((s) => s.key === sortKey) ?? SORTS[0]!,
    [sortKey],
  );

  const queryParams = {
    search: search || undefined,
    status: (filters.status || undefined) as CaseStatus | undefined,
    actionGroupId: filters.actionGroupId || undefined,
    actionTypeId: filters.actionTypeId || undefined,
    nature: (filters.nature || undefined) as CaseNature | undefined,
    tribunalCode: filters.tribunalCode || undefined,
    vara: filters.vara || undefined,
    openedFrom: filters.openedFrom || undefined,
    openedTo: filters.openedTo || undefined,
    page,
    pageSize: PAGE_SIZE,
    sortBy: sort.sortBy,
    sortDir: sort.sortDir,
  };

  const { data, isLoading, isError, isFetching } =
    useQuery<CasesListResponse>({
      queryKey: ['cases', 'list', queryParams],
      queryFn: () => apiClient.casesList(queryParams),
      placeholderData: (prev) => prev,
    });

  const summaryQuery = useQuery<CasesSummaryResponse>({
    queryKey: ['cases', 'stats', 'summary'],
    queryFn: () => apiClient.casesStatsSummary(),
    staleTime: 1000 * 60,
  });

  const items: CaseListItem[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const summary = summaryQuery.data;

  function handleExport() {
    window.alert('Exportação chega em breve.');
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <button
            type="button"
            data-tour="processos-novo"
            onClick={() => navigate('/escritorio/processos/novo')}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition',
              'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
            )}
          >
            <Plus className="h-3.5 w-3.5" />
            Novo processo
          </button>
        </div>
      </header>

      {summary && (
        <div data-tour="processos-stats-pills" className="flex flex-wrap gap-2">
          <StatPill label="Ativos" value={summary.totalActive} tone="active" />
          <StatPill
            label="Suspensos"
            value={summary.totalSuspended}
            tone="suspended"
          />
          <StatPill
            label="Arquivados"
            value={summary.totalArchived}
            tone="archived"
          />
          <StatPill
            label="Encerrados"
            value={summary.totalClosed}
            tone="closed"
          />
        </div>
      )}

      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div data-tour="processos-busca" className="flex items-center">
            {showSearch ? (
              <div className="flex items-center gap-1">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--fg-muted)]" />
                  <input
                    autoFocus
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Buscar por CNJ ou nome de parte..."
                    className="h-8 w-72 rounded-md border border-[var(--border)] bg-[var(--surface)] pl-7 pr-2 text-xs text-[var(--fg)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setSearch('');
                    setShowSearch(false);
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--fg-muted)] hover:bg-[var(--surface-muted)]"
                  aria-label="Fechar busca"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowSearch(true)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition',
                  'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
                )}
              >
                <Search className="h-3.5 w-3.5" />
                Buscar
              </button>
            )}
            </div>
            <FilterDropdown
              filters={filters}
              onChange={(next) => {
                setFilters((prev) => ({ ...prev, ...next }));
                setPage(1);
              }}
            />
            <SortDropdown current={sortKey} onChange={setSortKey} />
            <GlassDateRangePicker
              from={filters.openedFrom}
              to={filters.openedTo}
              onChange={({ from, to }) => {
                setFilters((prev) => ({
                  ...prev,
                  openedFrom: from,
                  openedTo: to,
                }));
                setPage(1);
              }}
              placeholder="Abertura"
              className="w-56"
              aria-label="Filtro por período de abertura"
            />
            <button
              type="button"
              onClick={handleExport}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition',
                'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
              )}
            >
              <Download className="h-3.5 w-3.5" />
              Exportar
            </button>
          </div>
          <div className="text-xs text-[var(--fg-muted)]">
            {isFetching
              ? 'Atualizando...'
              : `${total} processo${total === 1 ? '' : 's'}`}
          </div>
        </div>

        <div data-tour="processos-tabela" className="overflow-hidden rounded-xl border border-[var(--card-border)] bg-[var(--card-bg)] shadow-[var(--shadow-card)]">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--surface-muted)]/40 text-[10px] font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
                  <th className="px-4 py-2.5">Número CNJ</th>
                  <th className="px-4 py-2.5">Tipo de ação</th>
                  <th className="px-4 py-2.5">Parte principal</th>
                  <th className="px-4 py-2.5">Fase atual</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Abertura</th>
                </tr>
              </thead>
              {isLoading ? (
                <TableSkeleton />
              ) : isError ? (
                <tbody>
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-12 text-center text-sm text-[var(--fg-muted)]"
                    >
                      Falha ao carregar processos. Tente novamente.
                    </td>
                  </tr>
                </tbody>
              ) : items.length === 0 ? (
                <EmptyRow />
              ) : (
                <tbody>
                  {items.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => navigate(`/escritorio/processos/${c.id}`)}
                      className="cursor-pointer border-b border-[var(--border)] transition hover:bg-[var(--surface-muted)]/30"
                    >
                      <td className="px-4 py-3 font-mono text-[11px] text-[var(--fg)]">
                        {formatCnj(c.numeroCnj)}
                      </td>
                      <td className="px-4 py-3 text-[var(--fg)]">
                        {c.actionTypeName ?? '--'}
                      </td>
                      <td className="px-4 py-3 text-[var(--fg-muted)]">
                        {c.mainPartyNames || '--'}
                      </td>
                      <td className="px-4 py-3 text-[var(--fg-muted)]">
                        {c.currentStageName ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={c.status} />
                      </td>
                      <td className="px-4 py-3 text-[var(--fg-muted)]">
                        {formatOpenedAt(c.openedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
          {items.length > 0 && (
            <div className="flex items-center justify-between gap-3 border-t border-[var(--border)] px-4 py-2.5 text-xs text-[var(--fg-muted)]">
              <span>
                Página {page} de {totalPages}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={cn(
                    'inline-flex h-7 w-7 items-center justify-center rounded-md border transition',
                    'border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-muted)]',
                    page <= 1 && 'cursor-not-allowed opacity-40',
                  )}
                  aria-label="Página anterior"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={cn(
                    'inline-flex h-7 w-7 items-center justify-center rounded-md border transition',
                    'border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-muted)]',
                    page >= totalPages && 'cursor-not-allowed opacity-40',
                  )}
                  aria-label="Próxima página"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
